<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useTranslation } from 'i18next-vue'
import { useRouter } from 'vue-router'
import { useAsyncState } from '@vueuse/core'
import { useNotificationStore } from '@/stores/notification.store'
import { useDate } from '@/composables/useDate'
import type { Notification } from '@/types/notification'

import Button from 'primevue/button'
import OverlayBadge from 'primevue/overlaybadge'
import Popover from 'primevue/popover'
import ProgressSpinner from 'primevue/progressspinner'

const { t } = useTranslation()
const router = useRouter()
const notificationStore = useNotificationStore()
const { formatRelativeTime } = useDate()

const popover = ref()

const notificationIcon = 'ti-bell-ringing'
const notificationColor = 'text-primary'

/**
 * Toggle popover visibility
 */
function toggle(event: Event) {
    popover.value.toggle(event)
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        toggle(event)
    }

    if (event.key === ' ') {
        event.preventDefault()
        toggle(event)
    }
}

/** Mark single notification as read */
const { execute: markAsRead } = useAsyncState(
    async (notification: Notification) => {
        if (!notification.read) {
            await notificationStore.markAsRead(notification.documentId)
        }
    },
    undefined,
    { immediate: false }
)

/** Mark all notifications as read */
const { execute: markAllAsRead } = useAsyncState(
    async () => {
        await notificationStore.markAllAsRead()
    },
    undefined,
    { immediate: false }
)

/**
 * Handle notification click
 */
async function handleNotificationClick(notification: Notification) {
    await markAsRead(0, notification)

    if (notification.actionRoute) {
        router.push(notification.actionRoute)
    }

    popover.value.hide()
}

/**
 * Fetch notifications on mount
 */
onMounted(() => {
    notificationStore.fetchAll()
})
</script>

<template>
    <div class="relative">
        <OverlayBadge
            :value="notificationStore.unreadCount"
            class="cursor-pointer"
            role="button"
            tabindex="0"
            :aria-label="t('ui.notifications.openNotification')"
            data-testid="notification-bell-toggle-button"
            :pt="{
                root: {
                    class: 'min-w-4 h-4 text-[0.65rem] leading-4',
                },
                pcBadge: {
                    root: {
                        class: { 'hidden!': notificationStore.unreadCount === 0 },
                    },
                },
            }"
            @click="toggle"
            @keydown="handleKeydown"
        >
            <i class="pi pi-bell text-base" aria-hidden="true" />
        </OverlayBadge>

        <Popover ref="popover" :pt="{ content: { class: '!p-0' } }">
            <div class="w-95 max-w-[90vw]">
                <div class="flex justify-between items-center p-4 border-b">
                    <h3 class="heading-lg m-0">
                        {{
                            t('ui.notifications.displayCount', {
                                count: notificationStore.notifications.length,
                            })
                        }}
                    </h3>
                </div>

                <div class="max-h-100 overflow-y-auto">
                    <div
                        v-if="notificationStore.isLoading"
                        class="flex justify-center items-center p-8"
                    >
                        <ProgressSpinner />
                    </div>

                    <div
                        v-else-if="notificationStore.notifications.length === 0"
                        class="flex flex-col items-center justify-center p-8 text-center"
                    >
                        <i class="pi pi-bell text-4xl text-neutral-600 mb-3" aria-hidden="true" />
                        <p class="text-neutral-600 m-0">
                            {{ t('ui.notifications.empty') }}
                        </p>
                    </div>

                    <div v-else>
                        <div
                            v-for="notification in notificationStore.notifications"
                            :key="notification.documentId"
                            class="p-4 border-b last:border-b-0 hover:bg-surface-hover transition-colors"
                            :class="[
                                { 'bg-primary-subtle': !notification.read },
                                { 'cursor-pointer': !!notification.actionRoute },
                            ]"
                            data-testid="notification-bell-open-item"
                            role="button"
                            tabindex="0"
                            @click="handleNotificationClick(notification)"
                            @keydown.enter="handleNotificationClick(notification)"
                            @keydown.space.prevent="handleNotificationClick(notification)"
                        >
                            <div class="flex gap-3">
                                <div class="shrink-0">
                                    <i
                                        :class="[
                                            'ti',
                                            notificationIcon,
                                            notificationColor,
                                            'text-xl',
                                        ]"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="flex-1 min-w-0 space-y-1">
                                    <div class="flex items-start justify-between gap-2">
                                        <h4
                                            class="text-sm font-semibold m-0"
                                            :class="{
                                                'font-bold': !notification.read,
                                            }"
                                        >
                                            {{ notification.title }}
                                        </h4>
                                        <span
                                            v-if="!notification.read"
                                            class="shrink-0 w-2 h-2 bg-primary rounded-full"
                                            aria-label="Unread"
                                        ></span>
                                    </div>
                                    <p class="text-sm text-neutral-600 m-0">
                                        {{ notification.message }}
                                    </p>
                                    <span class="text-xs text-neutral-600">
                                        {{ formatRelativeTime(notification.createdAt) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="notificationStore.hasUnread" class="p-3 border-t text-right">
                    <Button
                        :label="t('ui.notifications.markAllAsRead')"
                        text
                        size="small"
                        data-testid="mark-all-read-button"
                        @click="markAllAsRead()"
                    />
                </div>
            </div>
        </Popover>
    </div>
</template>
